import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { loadBillingExempt } from '../../supabase/functions/_shared/billingExempt.ts';
import { normalizeAccountCurrency, type AccountCurrency } from '../../supabase/functions/_shared/accountCurrency.ts';
import { formatCreditsAsMoney } from '../../supabase/functions/_shared/billingPrices.ts';

export interface CreditInfo {
  credits: number;
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_status: string | null;
  grace_period_end: string | null;
  downgraded_at: string | null;
  credits_rollover: number;
  billingExempt: boolean;
  billingCurrency: AccountCurrency;
}

export interface CreditRate {
  id: string;
  rate_name: string;
  credits_per_minute: number;
  effective_from: string;
  is_active: boolean;
}

const DEFAULT_CREDIT_INFO: CreditInfo = {
  credits: 0,
  subscribed: false,
  subscription_tier: null,
  subscription_status: null,
  grace_period_end: null,
  downgraded_at: null,
  credits_rollover: 0,
  billingExempt: false,
  billingCurrency: 'gbp',
};

export const useCredits = () => {
  const [creditInfo, setCreditInfo] = useState<CreditInfo>(DEFAULT_CREDIT_INFO);
  const [currentRate, setCurrentRate] = useState<CreditRate | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchCredits();
      fetchCurrentRate();
    } else {
      setCreditInfo(DEFAULT_CREDIT_INFO);
      setCurrentRate(null);
      setLoading(false);
    }
  }, [user]);

  const fetchCredits = async () => {
    if (!user) return;

    const billingExempt = await loadBillingExempt(supabase, user.id);
    try {
      setLoading(true);
      const [{ data, error }, currencyResult] = await Promise.all([
        supabase.functions.invoke('check-subscription'),
        supabase.from('subscribers').select('billing_currency').eq('user_id', user.id).maybeSingle(),
      ]);
      
      if (error) throw error;
      const billingCurrency = normalizeAccountCurrency(currencyResult.data?.billing_currency);
      
      setCreditInfo({
        credits: data.credits || 0,
        subscribed: data.subscribed || false,
        subscription_tier: data.subscription_tier,
        subscription_status: data.subscription_status || null,
        grace_period_end: data.grace_period_end || null,
        downgraded_at: data.downgraded_at || null,
        credits_rollover: data.credits_rollover || 0,
        billingExempt,
        billingCurrency,
      });
    } catch (error: any) {
      console.error('Error fetching credits:', error);
      setCreditInfo((prev) => ({ ...prev, billingExempt }));
      toast({
        title: "Error",
        description: "Failed to load your balance",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentRate = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('credit_rates')
        .select('*')
        .eq('is_active', true)
        .eq('rate_name', 'standard_voice')
        .maybeSingle();

      if (error) throw error;
      
      setCurrentRate(data);
    } catch (error: any) {
      console.error('Error fetching credit rates:', error);
      toast({
        title: "Error",
        description: "Failed to fetch current credit rates",
        variant: "destructive",
      });
    }
  };

  // Returns true if the user's subscription is effectively downgraded
  const isEffectivelyDowngraded = (): boolean => {
    if (creditInfo.subscription_status === 'canceled') return true;
    if (creditInfo.subscription_status === 'past_due' && creditInfo.grace_period_end) {
      return new Date(creditInfo.grace_period_end) < new Date();
    }
    return false;
  };

  const isPastDue = (): boolean => {
    return creditInfo.subscription_status === 'past_due';
  };

  const gracePeriodDaysRemaining = (): number | null => {
    if (!creditInfo.grace_period_end || creditInfo.subscription_status !== 'past_due') return null;
    const end = new Date(creditInfo.grace_period_end);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const checkCanMakeCall = async (estimatedCreditsOrDuration: number = 5): Promise<boolean> => {
    if (!user) return false;
    
    await fetchCredits();
    if (await loadBillingExempt(supabase, user.id)) return true;
    
    const estimatedCredits = !currentRate || estimatedCreditsOrDuration > 100 
      ? estimatedCreditsOrDuration 
      : Math.ceil(estimatedCreditsOrDuration * currentRate.credits_per_minute);
    
    if (creditInfo.credits < estimatedCredits) {
      const currency = creditInfo.billingCurrency;
      toast({
        title: "Insufficient balance",
        description: `You need at least ${formatCreditsAsMoney(estimatedCredits, currency)} for this operation. Current balance: ${formatCreditsAsMoney(creditInfo.credits, currency)}`,
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const deductCallCredits = async (creditsOrDuration: number, callId?: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const creditsToDeduct = !currentRate || creditsOrDuration > 100 
        ? creditsOrDuration 
        : Math.ceil(creditsOrDuration * currentRate.credits_per_minute);

      const { data, error } = await supabase.functions.invoke('deduct-credits', {
        body: { 
          credits: creditsToDeduct,
          call_id: callId,
          duration_minutes: currentRate ? creditsOrDuration : undefined,
          rate_applied: currentRate?.credits_per_minute || 1
        }
      });

      if (error) throw error;

      setCreditInfo(prev => ({
        ...prev,
        credits: data.remainingCredits
      }));

      return true;
    } catch (error: any) {
      console.error('Error deducting credits:', error);
      toast({
        title: "Error",
        description: "Failed to update your balance",
        variant: "destructive",
      });
      return false;
    }
  };

  const calculateCallCost = (durationMinutes: number): number => {
    if (!currentRate) return 0;
    return Math.ceil(durationMinutes * currentRate.credits_per_minute);
  };

  const estimateCallsRemaining = (avgDurationMinutes: number = 5): number => {
    if (!currentRate) return 0;
    const creditsPerCall = Math.ceil(avgDurationMinutes * currentRate.credits_per_minute);
    return Math.floor(creditInfo.credits / creditsPerCall);
  };

  const getCreditRatePerMinute = (): number => {
    return currentRate?.credits_per_minute || 0;
  };

  return {
    ...creditInfo,
    currentRate,
    loading,
    fetchCredits,
    fetchCurrentRate,
    checkCanMakeCall,
    deductCallCredits,
    calculateCallCost,
    estimateCallsRemaining,
    getCreditRatePerMinute,
    isEffectivelyDowngraded,
    isPastDue,
    gracePeriodDaysRemaining,
  };
};
